// CTO Audit & Architecture document rendered as a page for in-app viewing.
export default function CTOAudit() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="cto-audit-page">
      <p className="overline">CTO audit · v1</p>
      <h1 className="font-display text-5xl mt-3">Architecture & Roadmap</h1>
      <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed">
        Strategic technical assessment of The Farm To You — a nationwide, membership-first, multi-vendor
        farm-to-door subscription commerce platform partnering with family farms across the United States.
      </p>

      <section className="mt-12 space-y-2">
        <h2 className="font-display text-3xl">1 · Current MVP Architecture</h2>
        <p className="text-[var(--ff-text-secondary)] leading-relaxed">
          Stack: <strong>React 19 (CRA + Tailwind), FastAPI (Python), MongoDB (Motor), Stripe Checkout via emergentintegrations.</strong> 
          Single-instance backend; UUID-keyed domain models; JWT auth in httpOnly cookies (+ token fallback).
          Production deploy target: Kubernetes-managed preview environment with `/api` routed to FastAPI.
        </p>
      </section>

      <section className="mt-10 space-y-2">
        <h2 className="font-display text-3xl">2 · Strengths shipped in MVP</h2>
        <ul className="list-disc pl-6 mt-3 text-[var(--ff-text-secondary)] space-y-1.5">
          <li>End-to-end membership gating (browse free, checkout requires plan).</li>
          <li>3-role RBAC (customer / vendor / admin) with seeded accounts.</li>
          <li>Stripe Checkout for memberships AND cart orders, with backend-enforced pricing.</li>
          <li>Vendor self-service product CRUD; admin vendor approvals.</li>
          <li>Server-side filtering, full-text search, popularity ranking.</li>
          <li>Premium editorial UI (Cormorant Garamond + Manrope, Florida palette).</li>
        </ul>
      </section>

      <section className="mt-10 space-y-2">
        <h2 className="font-display text-3xl">3 · Identified gaps & recommended fixes</h2>
        <div className="mt-4 space-y-4 text-[var(--ff-text-secondary)] leading-relaxed">
          <p><strong>Payments:</strong> MVP uses one-shot Stripe Checkout. Migrate to Stripe Billing (subscriptions) + Stripe Connect Express for vendor payouts. Add proration, dunning, ACH.</p>
          <p><strong>Fulfillment:</strong> No driver/route layer yet. Recommend Onfleet or Shipday API for last-mile, with cold-chain SLAs.</p>
          <p><strong>Search:</strong> Mongo regex is fine to ~10k SKUs. Beyond that, move to Meilisearch / Typesense for typo-tolerant filterable search.</p>
          <p><strong>Caching:</strong> Add Redis for product catalog (5-min TTL) and member session cache.</p>
          <p><strong>Observability:</strong> Add Sentry (FE+BE), Datadog APM, and an event pipeline (Segment → warehouse) for product analytics.</p>
          <p><strong>Auth:</strong> Add email verification, password reset, social OAuth, and TOTP 2FA for vendors/admins.</p>
          <p><strong>Storage:</strong> Move product images to S3 + CloudFront with signed uploads.</p>
        </div>
      </section>

      <section className="mt-10 space-y-2">
        <h2 className="font-display text-3xl">4 · Phased roadmap</h2>
        <div className="mt-4 space-y-3">
          {[
            ["Phase 1 — Today (MVP, shipped)", "Membership gating, marketplace, Stripe Checkout, customer/vendor/admin dashboards, seed data."],
            ["Phase 2 — 30 days", "Stripe Billing subscriptions, Connect Express onboarding, email/SMS via SendGrid + Twilio, S3 image uploads, address book, delivery scheduling."],
            ["Phase 3 — 60-90 days", "Onfleet/Shipday integration, driver app v1, route optimization, cold-chain tracking, referral & loyalty engine, gift memberships."],
            ["Phase 4 — 6 months", "Native iOS/Android (React Native or Expo), AI inventory forecasting, B2B/restaurant tier, white-label vendor storefronts."],
            ["Phase 5 — 12 months", "Multi-state expansion, distribution hub model, owned fleet, ML demand prediction, enterprise vendor analytics."],
          ].map(([p, d], i) => (
            <div key={i} className="card-flat p-5">
              <p className="font-semibold text-[var(--ff-primary)]">{p}</p>
              <p className="text-sm text-[var(--ff-text-secondary)] mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 space-y-2">
        <h2 className="font-display text-3xl">5 · Long-term tech stack vision</h2>
        <ul className="list-disc pl-6 mt-3 text-[var(--ff-text-secondary)] space-y-1.5">
          <li>Frontend: Next.js 15 (App Router) for SSR/SEO; React Native for mobile.</li>
          <li>Backend: FastAPI mono-service → modular services (orders, payments, fulfillment, catalog) as scale demands.</li>
          <li>Data: MongoDB for catalog/orders; Postgres for financial ledger; Redis cache; ClickHouse for analytics.</li>
          <li>Infra: AWS (ECS/Fargate or EKS), CloudFront, RDS, S3; Terraform IaC; GitHub Actions CI/CD.</li>
          <li>Security: WAF, AWS Secrets Manager, PCI-aware via Stripe (no card data touches our servers), audit logs in CloudTrail.</li>
        </ul>
      </section>
    </div>
  );
}
